/**
 * MarkdownRenderer Component
 * 
 * Renders markdown content with support for:
 * - Standard markdown elements (headings, lists, blockquotes, etc.)
 * - LaTeX math expressions (via KaTeX)
 * - Custom table rendering with export functionality
 * - SQL syntax highlighting
 */

import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import remarkBreaks from 'remark-breaks';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import i18n from '@/shared/lib/i18n';

import {
    splitContentByTables,
    hasMarkdownTables,
    escapeCurrencyForLatex,
    formatCellContent,
    isNumericHeader,
} from '@/shared/lib/markdown';
import { MarkdownTable } from './MarkdownTable';
import { CodeBlock } from './components/CodeBlock';
import { RechartsRenderer } from '@/shared/components/charts';


// =============================================================================
// Types
// =============================================================================

interface MarkdownRendererProps {
    content: string;
    className?: string;
}

interface MarkdownComponentProps {
    children?: React.ReactNode;
    className?: string;
    href?: string;
    node?: unknown;
}

// =============================================================================
// Memoized Component Maps (defined outside render for performance)
// =============================================================================

/**
 * Creates the component map for ReactMarkdown.
 * Defined as a function to allow for consistent usage across renders.
 */
const createMarkdownComponents = () => ({
    // Headings
    h1: ({ children }: MarkdownComponentProps) => (
        <h1 className="text-2xl font-bold mb-4 text-slate-900 dark:text-slate-100">
            {children}
        </h1>
    ),
    h2: ({ children }: MarkdownComponentProps) => (
        <h2 className="text-xl font-semibold mb-3 text-slate-900 dark:text-slate-100">
            {children}
        </h2>
    ),
    h3: ({ children }: MarkdownComponentProps) => (
        <h3 className="text-lg font-medium mb-2 text-slate-900 dark:text-slate-100">
            {children}
        </h3>
    ),

    // Paragraphs
    p: ({ children }: MarkdownComponentProps) => (
        <p className="mb-2 leading-relaxed text-slate-700 dark:text-slate-300">
            {children}
        </p>
    ),

    // Lists
    ul: ({ children }: MarkdownComponentProps) => (
        <ul className="list-disc list-outside mb-2 space-y-1">
            {children}
        </ul>
    ),
    ol: ({ children }: MarkdownComponentProps) => (
        <ol className="list-decimal list-outside mb-2 space-y-1">
            {children}
        </ol>
    ),
    li: ({ children }: MarkdownComponentProps) => (
        <li className="text-slate-700 dark:text-slate-300">
            {children}
        </li>
    ),

    // Inline formatting
    strong: ({ children }: MarkdownComponentProps) => (
        <strong className="font-semibold text-slate-900 dark:text-slate-100">
            {children}
        </strong>
    ),
    em: ({ children }: MarkdownComponentProps) => (
        <em className="italic text-slate-600 dark:text-slate-400">
            {children}
        </em>
    ),

    // Code (uses CodeBlock component or RechartsRenderer)
    code: ({ children, className }: MarkdownComponentProps) => {
        const match = /language-(\w+)/.exec(className || '');
        const language = match ? match[1] : '';
        const content = String(children).replace(/\n$/, '');

        if (language === 'recharts') {
            try {
                // Limpar o JSON antes de parsear (remover caracteres problemáticos)
                const cleanedContent = content
                    .replace(/[\x00-\x1F\x7F]/g, '') // Remove control characters
                    .replace(/\uFEFF/g, '') // Remove BOM characters
                    .replace(/\u200B/g, '') // Remove zero-width spaces
                    .replace(/\\\$/g, '$') // Fix escaped dollar signs (LaTeX: R\$ → R$)
                    .replace(/,\s*}/g, '}') // Remove trailing commas
                    .replace(/,\s*]/g, ']') // Remove trailing commas in arrays
                    .trim();

                const config = JSON.parse(cleanedContent);
                return (
                    <div className="my-4">
                        <RechartsRenderer config={config} title={config.title} />
                    </div>
                );
            } catch (e) {
                console.error("Failed to parse recharts JSON:", e);
                return (
                    <div className="p-4 border border-red-200 bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 rounded space-y-2">
                        <p className="font-medium">{i18n.t("shared:markdown.chartRenderError")}</p>
                        <details className="text-xs">
                            <summary className="cursor-pointer hover:underline">{i18n.t("shared:markdown.viewErrorDetails")}</summary>
                            <pre className="mt-2 p-2 bg-red-100 dark:bg-red-900/30 rounded overflow-x-auto max-h-40">
                                {content.substring(0, 300)}...
                            </pre>
                        </details>
                    </div>
                );
            }
        }

        return <CodeBlock className={className}>{children}</CodeBlock>;
    },
    pre: ({ children }: MarkdownComponentProps) => (
        <div className="mb-4">{children}</div>
    ),

    // Blockquote
    blockquote: ({ children }: MarkdownComponentProps) => (
        <blockquote className="border-l-4 border-blue-500 pl-4 py-2 mb-4 bg-blue-50 dark:bg-blue-950/30 italic text-slate-700 dark:text-slate-300">
            {children}
        </blockquote>
    ),

    // Links
    a: ({ children, href }: MarkdownComponentProps) => (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline"
        >
            {children}
        </a>
    ),

    // Tables (fallback for ReactMarkdown's built-in table handling)
    table: ({ children }: MarkdownComponentProps) => (
        <div className="overflow-x-auto mb-4 border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm">
            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                {children}
            </table>
        </div>
    ),
    thead: ({ children }: MarkdownComponentProps) => (
        <thead className="bg-slate-50 dark:bg-slate-800">
            {children}
        </thead>
    ),
    tbody: ({ children }: MarkdownComponentProps) => (
        <tbody className="bg-white dark:bg-slate-900 divide-y divide-slate-200 dark:divide-slate-700">
            {children}
        </tbody>
    ),
    tr: ({ children }: MarkdownComponentProps) => (
        <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors duration-150">
            {children}
        </tr>
    ),
    th: ({ children }: MarkdownComponentProps) => {
        const content = String(children || '');
        const isNumeric = isNumericHeader(content);
        return (
            <th className={`px-6 py-3 text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 ${isNumeric ? 'text-right' : 'text-left'}`}>
                {children}
            </th>
        );
    },
    td: ({ children }: MarkdownComponentProps) => {
        const content = String(children || '');
        const { type } = formatCellContent(content);
        const isNumericType = type !== 'text';

        let colorClass = 'text-slate-900 dark:text-slate-100';
        if (type === 'currency') colorClass = 'text-accent';
        if (type === 'percentage') colorClass = 'text-blue-700 dark:text-blue-400';

        return (
            <td className={`px-6 py-4 text-sm border-b border-slate-200 dark:border-slate-700 ${isNumericType ? 'text-right font-mono font-medium' : 'text-left'} ${colorClass}`}>
                {children}
            </td>
        );
    },
});

// Create components once (memoized at module level)
const markdownComponents = createMarkdownComponents();

// =============================================================================
// Main Component
// =============================================================================

/**
 * Renders markdown content with enhanced styling and features.
 * 
 * Features:
 * - Automatic table detection and custom rendering
 * - LaTeX math support (only when needed)
 * - SQL syntax highlighting
 * - Proper handling of Brazilian currency symbols
 */
export function MarkdownRenderer({ content, className = '' }: MarkdownRendererProps) {
    // Check if content has tables that need custom rendering
    const containsTables = useMemo(() => hasMarkdownTables(content), [content]);

    // If content has tables, split and render with MarkdownTable
    if (containsTables) {
        return <ContentWithTables content={content} className={className} />;
    }

    // Otherwise, render normally
    return <NormalContent content={content} className={className} />;
}

// =============================================================================
// Internal Components
// =============================================================================

interface ContentProps {
    content: string;
    className: string;
}

/**
 * Renders content that contains markdown tables.
 * Splits content into parts and renders tables with MarkdownTable component.
 */
const ContentWithTables: React.FC<ContentProps> = ({ content, className }) => {
    const parts = useMemo(() => splitContentByTables(content), [content]);

    if (parts.length === 0) {
        return <NormalContent content={content} className={className} />;
    }

    return (
        <div className={`markdown-content ${className}`}>
            {parts.map((part, index) => {
                if (part.type === 'table') {
                    return <MarkdownTable key={index} content={part.content} />;
                }
                return (
                    <div key={index}>
                        <NormalContent content={part.content} className="" />
                    </div>
                );
            })}
        </div>
    );
};

/**
 * Renders normal markdown content (without custom table handling).
 * Automatically enables LaTeX support when math expressions are detected.
 */
const NormalContent: React.FC<ContentProps> = ({ content: rawContent, className }) => {
    // Escape currency symbols for LaTeX compatibility
    const content = useMemo(
        () => escapeCurrencyForLatex(rawContent),
        [rawContent]
    );

    // Check if content contains LaTeX
    const hasLatex = useMemo(
        () => /\$.*\$|\\[a-zA-Z]+/.test(content),
        [content]
    );

    // Render without LaTeX plugins for performance when not needed
    if (!hasLatex) {
        return (
            <div className={`markdown-content ${className}`}>
                <ReactMarkdown
                    remarkPlugins={[remarkBreaks]}
                    components={markdownComponents}
                    skipHtml={false}
                >
                    {content}
                </ReactMarkdown>
            </div>
        );
    }

    // Render with LaTeX support
    return (
        <div className={`markdown-content ${className}`}>
            <ReactMarkdown
                remarkPlugins={[remarkMath, remarkBreaks]}
                rehypePlugins={[
                    [rehypeKatex, {
                        throwOnError: false,
                        errorColor: '#cc0000',
                        strict: false,
                    }]
                ]}
                components={markdownComponents}
                skipHtml={false}
            >
                {content}
            </ReactMarkdown>
        </div>
    );
};
